import ENV from "../../env";
import { DEFAULT_LOCALE, parseSupportedLocales, type SupportedLocale } from "@/shared/culture/locales";
import { mergeTranslations, type TranslationDictionary } from "@/shared/culture/mergeTranslations";
import { resolveLocale } from "@/shared/culture/resolveLocale";
import { loadModuleTranslations, loadTranslations } from "@/shared/culture/translationLoader";
import { appI18n, appI18nInitialization } from "./i18n";
import type { TranslationModule } from "./translationModules.generated";

export type InitialCulture = {
  locale: SupportedLocale;
  translations: TranslationDictionary;
  preloadedModules: TranslationModule[];
};

function getInitialModulesForPath(pathname: string): TranslationModule[] {
  const authRoutes = ["/login", "/register", "/migrate", "/error"];
  const isAuthRoute = authRoutes.some((route) => pathname === route || pathname.startsWith(`${route}/`));

  return isAuthRoute ? ["auth"] : [];
}

export async function preloadInitialCulture(): Promise<InitialCulture> {
  await appI18nInitialization;

  const allowedLocales = parseSupportedLocales(ENV.SUPPORTED_LOCALES);
  const locale = resolveLocale(allowedLocales);
  const preloadedModules = getInitialModulesForPath(window.location.pathname);

  try {
    let translations = await loadTranslations(locale);

    if (preloadedModules.length > 0) {
      const moduleTranslations = await Promise.all(preloadedModules.map((moduleName) => loadModuleTranslations(locale, moduleName)));
      for (const moduleTranslation of moduleTranslations) {
        translations = mergeTranslations(translations, moduleTranslation);
      }
    }

    appI18n.addResourceBundle(locale, "translation", translations[locale] ?? {}, true, true);
    await appI18n.changeLanguage(locale);

    return { locale, translations, preloadedModules };
  } catch (error) {
    console.error(`Error preloading translations for locale ${locale}:`, error);
    await appI18n.changeLanguage(locale);

    return { locale: locale || DEFAULT_LOCALE, translations: {}, preloadedModules: [] };
  }
}
